/**
 * This is the top level view which the entire app is rendered in to
 */

import PropTypes from 'prop-types';
import React, { Component, StrictMode } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { defineMessages, intlShape, injectIntl } from 'react-intl';
import { hot } from 'react-hot-loader/root';
import * as logger from 'universal-logger';
import * as cookiePolicyHelper from 'helpers/cookiePolicyHelper';
import * as userTypeCookie from 'helpers/userTypeCookie';
import * as utmValues from 'helpers/utmValues';
import { deepExtract } from 'helpers/deep';
import { setupSupportChat, isSupportChatRoute } from 'helpers/chat';
import { CookieBanner } from 'components/Banner/Banners/CookieBanner';
import { DisclaimerModal } from 'components/DisclaimerModal/DisclaimerModal';
import { FooterContainer } from 'components/Footer/Footer';
import { FullScreenError } from 'components/Errors/FullScreenError';
import { RetainSDK } from 'components/RetainSDK/RetainSDK';
import { FullStorySDK } from 'components/FullStorySDK/FullStorySDK';
import { GoogleAnalytics } from 'components/GoogleAnalytics/GoogleAnalytics';
import { GoogleTagManager } from 'components/GoogleTagManager/GoogleTagManager';
import { MediaListener } from 'components/MediaListener';
import { ModalHostContainer } from 'components/ModalHost/ModalHostContainer';
import { SnowplowTracker } from 'components/SnowplowTracker/SnowplowTracker';
import { fallbackBoundary } from 'decorators/FallbackBoundary';
import { withSagas } from 'decorators/Sagas/Sagas';
import { OptimizelySDK } from 'vendors/Optimizely/OptimizelySDK';
import { SegmentSDK } from 'vendors/Segment/SegmentSDK';
import clientConfig from 'universal-config';
import { decorateWith, withName } from 'helpers/decorator';
import isMobile, { isWebview } from 'helpers/isMobile';
import { glsLocation, userProfile } from 'redux/saga/entities/bound';
import { toggleFlag } from 'redux/modules/page';
import { setIsClient } from 'redux/modules/device';
import { selectUserLocation } from 'redux/modules/locale';
import { BannerHost } from 'features/Banners/BannerHost';
import { watchAnnouncements, loadAnnouncements } from 'containers/Announcements/announcements.saga';
import { AppHeaderContainer } from 'components/AppHeader/AppHeader';
import { registerGlobalError, watchErrorHandler, watchReservationStatusChanged, doLoadInitialApp } from './app.saga';
import { showReferAFriendBanner, watchReferAFriendBanner } from './app-refer-a-friend.saga';

require('../../styles/main.scss');
require('../../styles/components.scss');

export const mapStateToProps = (state, ownProps) => {
  const { lat, lon } = selectUserLocation(state) || {};
  const userLocation = glsLocation.data(state, `${lat}_${lon}`) || {};
  const glsLocationId = deepExtract('location.id', userLocation, undefined);

  return {
    config: clientConfig(),
    continent: state.locale.continent,
    currentFullUrl: state.request.currentFullUrl,
    routerPath: ownProps.location.pathname + (ownProps.location.search || ''),
    hasHashInRouterPath: !!ownProps.location.hash,
    deviceType: state.request.deviceType,
    error: state.error,
    flags: state.page.flags,
    glsLocationId,
    query: state.request.query,
    userProfile: userProfile.data(state),
    visitorId: state.request.visitorId,
  };
};

export const mapDispatchToProps = {
  loadAnnouncements,
  registerGlobalError,
  showReferAFriendBanner,
  toggleFlag,
  setIsClient,
};

const messages = defineMessages({
  description: {
    id: 'App.description',
    defaultMessage: 'Work out at the best studios in your city',
  },
  ogTitle: {
    id: 'App.ogTitle',
    defaultMessage: 'ClassPass - Work out at the best studios in your city',
  },
  ogSite_name: {
    id: 'App.ogSite_name',
    defaultMessage: 'ClassPass',
  },
});

export class AppUnwrapped extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      error: null,
    };

    this.hasSeenCookiePolicy = cookiePolicyHelper.hasSeenCookiePolicy();
  }

  componentDidMount() {
    const {
      routerPath,
      userProfile: localUserProfile,
      setIsClient: localSetIsClient,
      showReferAFriendBanner: localShowReferAFriendBanner,
    } = this.props;

    utmValues.save(routerPath);
    userTypeCookie.setFromProfile(localUserProfile);

    if (__CLIENT__) {
      if (!this.hasSeenCookiePolicy) {
        cookiePolicyHelper.set(true);
      }

      // This should only be temporary
      // Chat is loaded in via from google tag manager, but requirements came up to support extra configuration which needs to be done here
      // There is a roadmap item to stop using google tag manager to inject chat and instead handle everything from this codebase
      if (isSupportChatRoute(this.props.routerPath)) {
        setupSupportChat();
      }
    }

    localShowReferAFriendBanner();
    this.showAnnouncements();
    localSetIsClient(true);
  }

  static getDerivedStateFromError(error) {
    return {
      error,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.error) {
      if (prevProps.routerPath !== this.props.routerPath) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          error: null,
        });
      } else if (prevState.error !== this.state.error) {
        this.props.registerGlobalError(this.state.error);
      }
    }

    if (__CLIENT__) {
      if (prevProps.routerPath !== this.props.routerPath) {
        if (!this.props.hasHashInRouterPath) {
          window.scrollTo(0, 0);
        }
        // This is needed temporarily to support extra chat configuration
        if (isSupportChatRoute(this.props.routerPath)) {
          setupSupportChat();
        }
      }
      if (!prevProps.userProfile && this.props.userProfile) {
        userTypeCookie.setFromProfile(this.props.userProfile);
      }
    }
  }

  componentDidCatch(error) {
    if (__SERVER__) {
      // If an error is generated in SSR rethrow it to be caught by the `global-error-handler`.
      throw error;
    }
  }

  handleDocumentClick = () => {
    const { flags, toggleFlag: localToggleFlag } = this.props;

    if (isMobile() && flags.headerAccountDropdownOpen) {
      localToggleFlag('headerAccountDropdownOpen');
    }
  };

  shouldShowCookiePolicy = () => {
    const { continent, flags, location } = this.props;

    return (
      __CLIENT__ &&
      continent === 'europe' &&
      !this.hasSeenCookiePolicy &&
      flags.showCookiePolicy &&
      !isWebview(location.search)
    );
  };

  showAnnouncements = () => {
    const { loadAnnouncements: localLoadAnnouncements } = this.props;

    localLoadAnnouncements();
  };

  render() {
    const {
      children,
      config,
      currentFullUrl,
      routerPath,
      deviceType,
      glsLocationId,
      query,
      routes,
      toggleFlag: localToggleFlag,
      userProfile: localUserProfile,
      visitorId,
      intl,
    } = this.props;

    const error = this.state.error || this.props.error;

    const app = (
      <>
        <MediaListener />
        <Helmet>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="description" content={intl.formatMessage(messages.description)} />
          <meta property="og:title" content={intl.formatMessage(messages.ogTitle)} />
          <meta property="og:url" content={currentFullUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={intl.formatMessage(messages.ogSite_name)} />
          <meta property="og:image" content={require('static/img/facebook-image-yoga.jpg')} />
          <meta property="fb:app_id" content={deepExtract('fb.appId', config)} />
          <meta property="p:domain_verify" content="ed77031fc6389d9c85c24dd3a1cad7d5" />
          <meta property="apple-mobile-web-app-capable" content="yes" />
          <meta property="mobile-web-app-capable" content="yes" />
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <link rel="shortcut icon" content="/favicon.ico" />
        </Helmet>
        <ModalHostContainer />

        <BannerHost category="announcement" />
        <AppHeaderContainer />
        <BannerHost category="notification" />

        <div className="body__content">
          {error ? <FullScreenError error={error} /> : children}
          {this.shouldShowCookiePolicy() && (
            // eslint-disable-next-line react/jsx-no-bind
            <DisclaimerModal onClose={localToggleFlag.bind(this, 'showCookiePolicy')} text={<CookieBanner />} />
          )}
        </div>
        <FooterContainer />
      </>
    );

    if (query.disableTrackers) {
      return app;
    }

    return (
      <StrictMode>
        <SnowplowTracker host={config.snowplow.host} appId={config.snowplow.appId} />
        {app}
        <GoogleAnalytics
          userId={localUserProfile && localUserProfile.id}
          currentPath={routerPath}
          accountKey={config.googleAnalytics.key}
          cookieDomain={config.googleAnalytics.cookieDomain}
        />
        <GoogleTagManager
          userId={localUserProfile && localUserProfile.id}
          containerId={config.googleTagManager.container}
          glsLocationId={glsLocationId}
        />
        <FullStorySDK
          currentUser={localUserProfile}
          currentPath={routerPath}
          defaultSampleSize={config.fullStory.defaultSampleSize}
          elevatedPaths={config.fullStory.elevatedPaths}
          elevatedSampleSize={config.fullStory.elevatedSampleSize}
          visitorId={visitorId}
        />
        <RetainSDK currentUser={localUserProfile} accountKey={config.retain.appKey} />
        <SegmentSDK
          accountKey={config.segment.key}
          currentPath={routerPath}
          currentUser={localUserProfile}
          deviceType={deviceType}
          msa={localUserProfile && localUserProfile.msa}
          routes={routes}
          userId={localUserProfile?.id}
        />
        <OptimizelySDK />
      </StrictMode>
    );
  }
}

AppUnwrapped.contextTypes = {
  store: PropTypes.object.isRequired,
};

AppUnwrapped.propTypes = {
  children: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  continent: PropTypes.string.isRequired,
  currentFullUrl: PropTypes.string.isRequired,
  enableSegment: PropTypes.bool,
  routerPath: PropTypes.string.isRequired,
  hasHashInRouterPath: PropTypes.bool.isRequired,
  deviceType: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool]),
  flags: PropTypes.object.isRequired,
  loadAnnouncements: PropTypes.func.isRequired,
  location: PropTypes.object,
  isLoading: PropTypes.bool,
  query: PropTypes.object.isRequired,
  registerGlobalError: PropTypes.func.isRequired,
  routes: PropTypes.array.isRequired,
  showReferAFriendBanner: PropTypes.func.isRequired,
  toggleFlag: PropTypes.func.isRequired,
  glsLocationId: PropTypes.number,
  userProfile: PropTypes.object,
  visitorId: PropTypes.string,
  setIsClient: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

// `renderFallback` is used in case the `App` component itself throws an error which
//  would not be caught in it's own error boundary.
//  This most likely would not be recoverable so we would show the fullscreen error component
//  and log the error.
// eslint-disable-next-line react/prop-types
export const renderFallback = ({ error }) => {
  logger.critical('App Component Error', error);

  return <FullScreenError error={error} />;
};

export const App = decorateWith(
  withSagas({
    load: doLoadInitialApp,
    watch: [watchAnnouncements, watchErrorHandler, watchReferAFriendBanner, watchReservationStatusChanged],
  }),
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl,
  hot,
  fallbackBoundary({ renderFallback }),
  withName('App')
)(AppUnwrapped);
